import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Button,
  CircularProgress
} from '@mui/material';

import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import { useNavigate } from 'react-router-dom';

import { AppLayout } from '../components/layout/AppLayout';
import { Loader } from '../components/common/Loader';
import { useApi } from '../hooks/useApi';
import { useClinic } from '../hooks/useClinic';
import { useAuthStore } from '../stores/authStore';

import {
  getPromotions,
  getPostImageUrl,
  Post
} from '../services/clinicService';

import {
  createAppointmentRequest
} from '../services/requestsService';

import { BackButton } from '../components/common/BackButton';
import { SuccessPopup } from '../components/common/SuccessPopup';

// Функция для безопасной обработки HTML в тексте акций
const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  // Заменяем переносы строк на <br />
  let sanitized = html.replace(/\n/g, '<br />');

  // Белый список разрешённых тегов
  const allowedTags = /<\/?[bi]>|<br\s*\/?>/gi;
  const onlyAllowed = sanitized.replace(/<[^>]+>/g, (tag) => {
    return allowedTags.test(tag) ? tag : '';
  });

  return onlyAllowed;
};

// ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ ДЛЯ ФОРМАТИРОВАНИЯ ДАТЫ
/**
 * Форматирует дату окончания акции в читаемый вид
 * @param endDate - дата в формате ISO string
 * @returns строка вида "31 декабря" или "31 декабря 2026"
 */
const formatEndDate = (endDate?: string): string => {
  if (!endDate) return '';

  const end = new Date(endDate);
  const now = new Date();

  const months = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
  ];

  const day = end.getDate();
  const month = months[end.getMonth()];
  const year = end.getFullYear();
  const currentYear = now.getFullYear();

  // Если год окончания больше текущего года — показываем год
  if (year > currentYear) {
    return `${day} ${month} ${year}`;
  }

  // Если в текущем году — только день и месяц
  return `${day} ${month}`;
};

// ОСНОВНОЙ КОМПОНЕНТ
export const PromotionsPage: React.FC = () => {

  const navigate = useNavigate();

  // Состояния компонента
  const [promotions, setPromotions] = useState<Post[]>([]);
  const [selectedPromo, setSelectedPromo] = useState<Post | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Хуки для получения данных о клинике и пользователе
  const { clinic, branches } = useClinic();
  const { user } = useAuthStore();

  // Находим текущий филиал пользователя
  const currentBranch = branches.find(b => b.id === user?.branchId);
  
  // Формируем название клиники для отображения
  const clinicName = currentBranch?.name || clinic?.name || 'Клиника';
  
  // Получаем расписание текущего филиала
  const workSchedule = currentBranch?.workSchedule || null;

  // Загрузка списка акций
  const { isLoading, error } = useApi(async () => {
    const data = await getPromotions();
    setPromotions(data.posts);
  }, { autoExecute: true });

  /**
   * Обработчик записи на акцию
   * @param promoId - ID акции
   */
  const handleBookPromo = async (promoId: string) => {
    setIsSubmitting(true);

    try {
      await createAppointmentRequest({
        promoId,
        from: 'webapp'
      });

      setShowDetails(false);
      setShowSuccessPopup(true);

    } catch (e: any) {
      setErrorMessage('Ошибка отправки заявки. Пожалуйста, попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Состояние загрузки
  if (isLoading) {
    return (
      <AppLayout title="Акции">
        <Loader message="Загрузка акций..." />
      </AppLayout>
    );
  }

  // Состояние ошибки загрузки
  if (error) {
    return (
      <AppLayout title="Акции">
        <Typography color="error" sx={{ textAlign: 'center' }}>
          Ошибка загрузки акций. Пожалуйста, обновите страницу.
        </Typography>
      </AppLayout>
    );
  }

  // РЕНДЕР ДЕТАЛЬНОЙ СТРАНИЦЫ АКЦИИ
  if (showDetails && selectedPromo) {
  return (
    <AppLayout title="Акция">
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: 'calc(100vh - 80px)', // вычитаем высоту хедера и отступы
        height: '100%'
      }}>
        
        {/* Контентная область — растягивается */}
        <Box sx={{ flex: 1 }}>
          <BackButton onClick={() => setShowDetails(false)} />

          {/* Изображение акции (если есть) */}
          {selectedPromo.image && (
            <Box
              sx={{
                width: '100%',
                height: { xs: 150, sm: 180, md: 220 },
                borderRadius: '16px',
                backgroundImage: `url(${getPostImageUrl(selectedPromo.image) as string})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                mb: { xs: 2, sm: 3 }
              }}
            />
          )}

          {/* Текст акции */}
          <Box
            sx={{
              fontSize: { xs: 13, sm: 14 },
              lineHeight: 1.6,
              mb: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              '& b': { fontWeight: 700 },
              '& br': { display: 'block', content: '""', height: '0.5em' }
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(selectedPromo.content)
            }}
          />
        </Box>

        {/* Кнопка записи — фиксированный отступ снизу */}
        <Box sx={{ pt: { xs: 2, sm: 3 }, pb: '30px' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleBookPromo(selectedPromo.id)}
            disabled={isSubmitting}
            sx={{
              height: { xs: 48, sm: 54, md: 55 },
              borderRadius: '22px',
              backgroundColor: '#007AFF',
              textTransform: 'none',
              fontSize: { xs: 14, sm: 16 }
            }}
          >
            {isSubmitting
              ? <CircularProgress size={22} color="inherit" />
              : 'Записаться на приём'}
          </Button>
        </Box>

      </Box>
    </AppLayout>
  );
}

  // РЕНДЕР СПИСКА АКЦИЙ
  return (
    <AppLayout title="Акции">

      <BackButton onClick={() => navigate('/')} />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '768px' } }}>
          <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, fontWeight: 600, mb: 1 }}>
            Акции
          </Typography>

          {/* Сетка карточек акций */}
          <Grid container spacing={2}>
          {promotions.map(promo => {
            const endDate = (promo as any).validUntil;
            const dateLabel = formatEndDate(endDate);
            const hasImage = promo.image && getPostImageUrl(promo.image);

            return (
              <Grid item xs={12} sm={6} md={6} lg={4} key={promo.id}>
                {/* Плитка акции */}
                <Card
                  onClick={() => {
                    setSelectedPromo(promo);
                    setShowDetails(true);
                  }}
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    background: '#fff',
                    transition: '0.2s',
                    '&:active': { transform: 'scale(0.98)' }
                  }}
                >
                {/* Блок с картинкой — только если есть изображение */}
                {hasImage && (
                  <Box sx={{ position: 'relative', width: '100%', height: '170px' }}>
                    <img
                      src={getPostImageUrl(promo.image) as string}
                      alt="promo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Бабл длительности: сверху слева, адаптивный */}
                    {dateLabel && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 8, sm: 12 },
                          left: { xs: 8, sm: 12 },
                          maxWidth: { xs: '75%', sm: '255px' },
                          px: { xs: 1, sm: 2 },
                          py: { xs: 0.5, sm: 1 },
                          backgroundColor: '#007AFF',
                          borderRadius: '21.5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: '11px', sm: '12px' },
                            fontWeight: 600,
                            color: '#FFFFFF',
                            textAlign: 'center'
                          }}
                        >
                          Предложение действует до {dateLabel}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Если нет картинки — бабл просто сверху */}
                {!hasImage && dateLabel && (
                  <Box
                    sx={{
                      maxWidth: { xs: '75%', sm: '255px' },
                      px: { xs: 1, sm: 2 },
                      py: { xs: 0.5, sm: 1 },
                      backgroundColor: '#007AFF',
                      borderRadius: '21.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 2 },
                      mt: { xs: 0.5, sm: 1 },
                      mx: 'auto'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '11px', sm: '13px' },
                        fontWeight: 600,
                        color: '#FFFFFF',
                        textAlign: 'center'
                      }}
                    >
                      До {dateLabel}
                    </Typography>
                  </Box>
                )}

               {/* Контент карточки */}
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    fontSize: { xs: 13, sm: 14 },
                    color: '#212121',
                    mb: 1,
                    lineHeight: 1.4,
                    maxWidth: '100%',
                    '& b': { fontWeight: 700 },
                    '& i': { fontStyle: 'italic' }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(promo.content || '')
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <Typography sx={{ color: '#007AFF', fontWeight: 600, fontSize: { xs: 13, sm: 14 } }}>
                    Записаться
                  </Typography>
                  <ArrowOutwardIcon sx={{ color: '#007AFF', fontSize: { xs: 16, sm: 18 } }} />
                </Box>
              </CardContent>
              </Card>
            </Grid>
          );
          })}
          </Grid>
        </Box>
      </Box>

      <SuccessPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        clinicName={clinicName}
        workSchedule={workSchedule}
      />

      {/* Уведомление об ошибке */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>

    </AppLayout>
  );
};